/**
 * Coach-tip tracker (doc 147 §5.3 PR F · onboarding micro-coaches).
 *
 * Tracks per-feature visit counts in localStorage so first-time tips
 * fade out after the user has seen them a few times. Designed to live
 * next to the affordance they're explaining — see the `?` glyphs on the
 * entity insight tiles.
 *
 * Storage keys: `atlas.coach.<featureId>` → JSON `{ visits, dismissed }`.
 */
export const COACH_HIDE_AFTER = 3;
const KEY_PREFIX = 'atlas.coach.';

interface CoachState {
  visits: number;
  dismissed: boolean;
}

function read(featureId: string): CoachState {
  try {
    const raw = localStorage.getItem(KEY_PREFIX + featureId);
    if (!raw) return { visits: 0, dismissed: false };
    const parsed = JSON.parse(raw) as Partial<CoachState>;
    return {
      visits: typeof parsed.visits === 'number' ? parsed.visits : 0,
      dismissed: Boolean(parsed.dismissed),
    };
  } catch {
    return { visits: 0, dismissed: false };
  }
}

function write(featureId: string, state: CoachState): void {
  try {
    localStorage.setItem(KEY_PREFIX + featureId, JSON.stringify(state));
  } catch {
    /* hostile env (SSR, disabled storage) — silent. */
  }
}

export function shouldShowTip(featureId: string): boolean {
  const s = read(featureId);
  return !s.dismissed && s.visits < COACH_HIDE_AFTER;
}

export function recordTipShown(featureId: string): void {
  const s = read(featureId);
  write(featureId, { ...s, visits: s.visits + 1 });
}

export function dismissTip(featureId: string): void {
  const s = read(featureId);
  write(featureId, { ...s, dismissed: true });
}
